import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const WishlistItemCard = ({ 
  item, 
  onRemove, 
  onPriorityChange, 
  onAddNote, 
  onToggleAlert,
  isSelected = false,
  onSelect = () => {}
}) => {
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState(item?.note || '');
  const navigate = useNavigate();

  const priorityOptions = [
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const handleSaveNote = () => {
    onAddNote(item?.id, noteText);
    setShowNoteInput(false);
  };

  const handleViewDetails = () => {
    navigate('/upgrade-recommendations', { 
      state: { selectedItem: item } 
    });
  };

  const getPriceChangeColor = (change) => {
    if (change > 0) return 'text-error';
    if (change < 0) return 'text-success';
    return 'text-muted-foreground';
  };

  const getPriceChangeIcon = (change) => {
    if (change > 0) return 'TrendingUp';
    if (change < 0) return 'TrendingDown';
    return 'Minus';
  };

  const formatPriceChange = (change) => {
    const absChange = Math.abs(change);
    const sign = change > 0 ? '+' : change < 0 ? '-' : '';
    return `${sign}$${absChange?.toFixed(2)}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-all duration-200">
      {/* Header with selection and priority */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(item?.id, e?.target?.checked)}
            className="w-4 h-4 text-accent border-border rounded focus:ring-2 focus:ring-accent"
          />
          <div className="flex-1">
            <Select
              options={priorityOptions}
              value={item?.priority}
              onChange={(value) => onPriorityChange(item?.id, value)}
              className="w-32"
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onToggleAlert(item?.id)}
            className={`p-2 rounded-full transition-colors interactive-scale ${
              item?.priceAlert 
                ? 'bg-accent text-accent-foreground' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            title={item?.priceAlert ? 'Price alerts enabled' : 'Enable price alerts'}
          >
            <Icon name="Bell" size={16} />
          </button>
          <button
            onClick={() => onRemove(item?.id)}
            className="p-2 rounded-full bg-error/10 text-error hover:bg-error/20 transition-colors interactive-scale"
            title="Remove from wishlist"
          >
            <Icon name="Trash2" size={16} />
          </button>
        </div>
      </div>
      {/* Product Info */}
      <div className="flex space-x-4 mb-4">
        <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-md bg-muted">
          <Image
            src={item?.image}
            alt={item?.name}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground text-sm mb-1 truncate">
            {item?.name}
          </h3>
          <p className="text-xs text-muted-foreground mb-2">
            {item?.category} • {item?.brand}
          </p>
          
          {/* Compatibility Status */}
          <div className="flex items-center space-x-2 mb-2">
            <Icon 
              name={item?.compatibility === 'perfect' ? 'CheckCircle' : 
                    item?.compatibility === 'good' ? 'AlertCircle' : 'XCircle'} 
              size={14} 
              color={item?.compatibility === 'perfect' ? 'var(--color-success)' : 
                     item?.compatibility === 'good' ? 'var(--color-warning)' : 'var(--color-error)'}
            />
            <span className="text-xs text-muted-foreground">
              {item?.compatibility === 'perfect' ? 'Perfect Match' : 
               item?.compatibility === 'good' ? 'Good Match' : 'Requires Adapter'}
            </span>
          </div>

          {/* Tags */}
          {item?.tags && item?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item?.tags?.slice(0, 3)?.map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
              {item?.tags?.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{item?.tags?.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Price Information */}
      <div className="bg-muted/50 rounded-md p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className="text-lg font-bold text-foreground">
              ${item?.currentPrice?.toFixed(2)}
            </span>
            {item?.originalPrice > item?.currentPrice && (
              <span className="text-sm text-muted-foreground line-through ml-2">
                ${item?.originalPrice?.toFixed(2)}
              </span>
            )}
          </div>
          
          {item?.priceChange !== 0 && (
            <div className={`flex items-center space-x-1 ${getPriceChangeColor(item?.priceChange)}`}>
              <Icon name={getPriceChangeIcon(item?.priceChange)} size={14} />
              <span className="text-sm font-medium">
                {formatPriceChange(item?.priceChange)}
              </span>
            </div>
          )}
        </div>
        
        <div className="text-xs text-muted-foreground">
          Added {item?.dateAdded} • Last updated {item?.lastUpdated}
        </div>
      </div>
      {/* Vendor Comparison */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            Available at {item?.vendors?.length} vendors
          </span>
          <button
            onClick={handleViewDetails}
            className="text-xs text-accent hover:text-accent/80 transition-colors"
          >
            Compare prices
          </button>
        </div>
        
        <div className="flex space-x-2">
          {item?.vendors?.slice(0, 3)?.map((vendor, index) => (
            <div key={index} className="flex-1 text-center">
              <div className="text-xs text-muted-foreground">{vendor?.name}</div>
              <div className="text-sm font-medium text-foreground">
                ${vendor?.price?.toFixed(2)}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Note Section */}
      <div className="mb-4">
        {item?.note && !showNoteInput ? (
          <div className="bg-background border border-border rounded-md p-2">
            <div className="flex items-start justify-between">
              <p className="text-sm text-foreground flex-1">{item?.note}</p>
              <button
                onClick={() => setShowNoteInput(true)}
                className="ml-2 p-1 hover:bg-muted rounded transition-colors interactive-scale"
              >
                <Icon name="Edit2" size={14} />
              </button>
            </div>
          </div>
        ) : showNoteInput ? (
          <div className="space-y-2">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e?.target?.value)}
              placeholder="Add a personal note about this item..."
              className="w-full px-3 py-2 border border-input rounded-md text-sm resize-none focus:ring-2 focus:ring-ring focus:border-transparent"
              rows={2}
            />
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="xs"
                onClick={() => setShowNoteInput(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="xs"
                onClick={handleSaveNote}
              >
                Save Note
              </Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowNoteInput(true)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center space-x-1"
          >
            <Icon name="Plus" size={14} />
            <span>Add note</span>
          </button>
        )}
      </div>
      {/* Action Buttons */}
      <div className="flex space-x-2">
        <Button
          variant="default"
          size="sm"
          onClick={handleViewDetails}
          className="flex-1"
          iconName="ExternalLink"
          iconPosition="right"
        >
          View Details
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(item?.vendors?.[0]?.url, '_blank')}
          iconName="ShoppingCart"
          iconPosition="left"
        >
          Buy Now
        </Button>
      </div>
    </div>
  );
};

export default WishlistItemCard;